import { useState, useEffect, useMemo } from 'react';
import type { Goal, GoalType, GoalPriority, GoalStatus } from '../../../hooks/useGoals';
import { CurrencyInput } from '../../../components/base/CurrencyInput';

interface GoalModalProps {
  open: boolean;
  goal?: Goal | null;
  onClose: () => void;
  onSave: (values: Omit<Goal, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => Promise<{ error: string | null }>;
}

const GOAL_TYPES: { value: GoalType; label: string; icon: string; color: string; example: string }[] = [
  { value: 'reserva_emergencia', label: 'Reserva de emergência', icon: 'ri-shield-check-line', color: '#10b981', example: 'Ex: Reserva para 6 meses de despesas' },
  { value: 'viagem', label: 'Viagem', icon: 'ri-plane-line', color: '#0891b2', example: 'Ex: Viagem para Europa' },
  { value: 'quitar_divida', label: 'Quitar dívida', icon: 'ri-bill-line', color: '#ef4444', example: 'Ex: Quitar financiamento do carro' },
  { value: 'investimento', label: 'Investimento', icon: 'ri-line-chart-line', color: '#8b5cf6', example: 'Ex: Carteira de ações' },
  { value: 'entrada_imovel', label: 'Entrada de imóvel', icon: 'ri-home-line', color: '#f59e0b', example: 'Ex: Entrada do apartamento' },
  { value: 'compra_pessoal', label: 'Compra pessoal', icon: 'ri-shopping-bag-line', color: '#ec4899', example: 'Ex: Notebook novo' },
  { value: 'outro', label: 'Outro objetivo', icon: 'ri-trophy-line', color: '#64748b', example: 'Ex: Minha meta personalizada' },
];

const PRIORITIES: { value: GoalPriority; label: string; color: string }[] = [
  { value: 'high', label: 'Alta', color: 'text-red-600 bg-red-50 border-red-200' },
  { value: 'medium', label: 'Média', color: 'text-amber-600 bg-amber-50 border-amber-200' },
  { value: 'low', label: 'Baixa', color: 'text-gray-500 bg-gray-50 border-gray-200' },
];

const STATUSES: { value: GoalStatus; label: string }[] = [
  { value: 'active', label: 'Ativa' },
  { value: 'paused', label: 'Pausada' },
  { value: 'completed', label: 'Concluída' },
];

const EXTRA_COLORS = ['#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#0891b2', '#ec4899', '#64748b', '#047857'];

const ICONS = [
  'ri-home-line', 'ri-car-line', 'ri-plane-line', 'ri-graduation-cap-line',
  'ri-heart-line', 'ri-laptop-line', 'ri-trophy-line', 'ri-rocket-line',
  'ri-shield-check-line', 'ri-leaf-line', 'ri-gift-line', 'ri-line-chart-line',
];

const PACE_LABELS: Record<string, { label: string; color: string; icon: string }> = {
  completed: { label: 'Concluída', color: 'text-emerald-700', icon: 'ri-check-line' },
  on_track: { label: 'No ritmo', color: 'text-emerald-600', icon: 'ri-checkbox-circle-line' },
  at_risk: { label: 'Em risco', color: 'text-amber-600', icon: 'ri-error-warning-line' },
  behind: { label: 'Atrasada', color: 'text-red-500', icon: 'ri-time-line' },
  overdue: { label: 'Prazo vencido', color: 'text-red-600', icon: 'ri-alarm-warning-line' },
  no_date: { label: 'Sem prazo definido', color: 'text-gray-500', icon: 'ri-calendar-line' },
};

const calcProjection = (targetAmount: number, currentAmount: number, targetDate: string | null) => {
  const remaining = Math.max(0, targetAmount - currentAmount);
  const progress = targetAmount > 0 ? Math.min(100, (currentAmount / targetAmount) * 100) : 0;

  if (remaining <= 0) return { remaining: 0, progress: 100, monthsRemaining: null, monthlyNeeded: null, pace: 'completed' };
  if (!targetDate) return { remaining, progress, monthsRemaining: null, monthlyNeeded: null, pace: 'no_date' };

  const today = new Date();
  const target = new Date(targetDate + 'T12:00:00');
  if (target <= today) return { remaining, progress, monthsRemaining: 0, monthlyNeeded: null, pace: 'overdue' };

  const monthsRemaining = Math.max(0.5,
    (target.getFullYear() - today.getFullYear()) * 12 + (target.getMonth() - today.getMonth())
  );
  const monthlyNeeded = remaining / monthsRemaining;
  const pace = monthsRemaining >= 12 ? 'on_track' : monthsRemaining >= 3 ? 'at_risk' : 'behind';

  return { remaining, progress, monthsRemaining, monthlyNeeded, pace };
};

const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const defaultForm = (): Omit<Goal, 'id' | 'user_id' | 'created_at' | 'updated_at'> => ({
  name: '', type: 'outro', priority: 'medium', status: 'active',
  target_amount: 0, current_amount: 0,
  target_date: null, description: '',
  color: '#64748b', icon: 'ri-trophy-line', is_completed: false,
});

export const GoalModal = ({ open, goal, onClose, onSave }: GoalModalProps) => {
  const [form, setForm] = useState(defaultForm());
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (open) {
      if (goal) {
        setForm({
          name: goal.name ?? '', type: goal.type ?? 'outro',
          priority: goal.priority ?? 'medium', status: goal.status ?? 'active',
          target_amount: goal.target_amount ?? 0, current_amount: goal.current_amount ?? 0,
          target_date: goal.target_date ?? null, description: goal.description ?? '',
          color: goal.color ?? '#64748b', icon: goal.icon ?? 'ri-trophy-line',
          is_completed: goal.is_completed ?? false,
        });
      } else {
        setForm(defaultForm());
      }
      setErrorMsg('');
    }
  }, [open, goal]);

  const handleTypeSelect = (typeVal: GoalType) => {
    const t = GOAL_TYPES.find(x => x.value === typeVal);
    setForm(p => ({
      ...p,
      type: typeVal,
      icon: t?.icon ?? p.icon,
      color: t?.color ?? p.color,
    }));
  };

  const projection = useMemo(
    () => calcProjection(form.target_amount, form.current_amount, form.target_date),
    [form.target_amount, form.current_amount, form.target_date]
  );

  const paceInfo = PACE_LABELS[projection.pace] ?? PACE_LABELS['no_date'];
  const selectedType = GOAL_TYPES.find(t => t.value === form.type);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg('');
    setSaving(true);
    const result = await onSave(form);
    setSaving(false);
    if (result?.error) setErrorMsg('Erro ao salvar. Tente novamente.');
    else onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl max-w-md w-full mx-4 overflow-hidden max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            {form.icon && (
              <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ backgroundColor: form.color + '22' }}>
                <i className={`${form.icon} text-sm`} style={{ color: form.color }} />
              </div>
            )}
            <h3 className="text-base font-700 text-gray-900">{goal ? 'Editar Meta' : 'Nova Meta'}</h3>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer">
            <i className="ri-close-line" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5 overflow-y-auto flex-1">

          {/* Tipo */}
          <div>
            <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-2 block">Tipo de meta</label>
            <div className="grid grid-cols-2 gap-2">
              {GOAL_TYPES.map(t => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => handleTypeSelect(t.value)}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border-2 text-left transition-all cursor-pointer ${
                    form.type === t.value ? 'border-emerald-400 bg-emerald-50' : 'border-gray-100 hover:border-gray-200 bg-white'
                  }`}
                >
                  <div
                    className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: form.type === t.value ? t.color + '22' : '#f3f4f6' }}
                  >
                    <i className={`${t.icon} text-sm`} style={{ color: form.type === t.value ? t.color : '#9ca3af' }} />
                  </div>
                  <span className={`text-xs font-600 leading-tight ${form.type === t.value ? 'text-emerald-700' : 'text-gray-700'}`}>
                    {t.label}
                  </span>
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
              <i className="ri-palette-line text-xs" />
              Ícone e cor sugeridos automaticamente — personalize abaixo se quiser.
            </p>
          </div>

          {/* Nome */}
          <div>
            <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">Nome da meta *</label>
            <input
              required
              value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              placeholder={selectedType?.example ?? 'Ex: Minha meta...'}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-50"
            />
          </div>

          {/* Descrição */}
          <div>
            <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">
              Descrição <span className="normal-case text-gray-400 font-400">(opcional)</span>
            </label>
            <textarea
              rows={2} maxLength={300} value={form.description}
              onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
              placeholder="Detalhes sobre esse objetivo..."
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-400 resize-none"
            />
          </div>

          {/* Valores */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">Valor alvo (R$) *</label>
              <CurrencyInput
                required
                value={form.target_amount}
                onChange={(v) => setForm(p => ({ ...p, target_amount: v }))}
                placeholder="0,00"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-50"
              />
              <p className="text-xs text-gray-400 mt-1">Quanto você quer ter no total</p>
            </div>
            <div>
              <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">Já acumulado (R$)</label>
              <CurrencyInput
                value={form.current_amount}
                onChange={(v) => setForm(p => ({ ...p, current_amount: v }))}
                placeholder="0,00"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-50"
              />
              <p className="text-xs text-gray-400 mt-1">Quanto você já tem guardado</p>
            </div>
          </div>

          {/* Data alvo */}
          <div>
            <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">
              Data alvo <span className="normal-case text-gray-400 font-400">(opcional)</span>
            </label>
            <input
              type="date"
              value={form.target_date ?? ''}
              onChange={e => setForm(p => ({ ...p, target_date: e.target.value || null }))}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-400"
            />
            <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
              <i className="ri-calculator-line text-xs" />
              Com uma data, calculamos automaticamente quanto guardar por mês.
            </p>
          </div>

          {/* Projeção automática ao vivo */}
          {form.target_amount > 0 && (
            <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
              <p className="text-xs font-700 text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                <i className="ri-bar-chart-line text-gray-400" />
                Projeção estimada
              </p>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Valor faltante</p>
                  <p className="text-sm font-700 text-gray-800">{fmt(projection.remaining)}</p>
                </div>
                {projection.monthsRemaining !== null && (
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Meses restantes</p>
                    <p className="text-sm font-700 text-gray-800">
                      {projection.monthsRemaining < 1 ? '&lt; 1 mês' : `${Math.round(projection.monthsRemaining)} ${Math.round(projection.monthsRemaining) === 1 ? 'mês' : 'meses'}`}
                    </p>
                  </div>
                )}
                {projection.monthlyNeeded !== null && (
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Aporte mensal sugerido</p>
                    <p className="text-sm font-700 text-emerald-600">{fmt(projection.monthlyNeeded)}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Progresso atual</p>
                  <p className="text-sm font-700 text-gray-800">{projection.progress.toFixed(0)}%</p>
                </div>
              </div>
              <div className={`flex items-center gap-2 text-xs font-600 ${paceInfo.color}`}>
                <i className={`${paceInfo.icon} text-sm`} />
                {paceInfo.label}
                {!form.target_date && projection.pace === 'no_date' && (
                  <span className="font-400 text-gray-400 ml-1">— defina uma data para ver o status</span>
                )}
              </div>
            </div>
          )}

          {/* Prioridade */}
          <div>
            <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-2 block">Prioridade</label>
            <div className="flex gap-2">
              {PRIORITIES.map(p => (
                <button
                  key={p.value} type="button"
                  onClick={() => setForm(prev => ({ ...prev, priority: p.value }))}
                  className={`flex-1 py-2 rounded-xl border text-sm font-600 transition-all cursor-pointer ${
                    form.priority === p.value ? p.color : 'text-gray-500 bg-white border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Status (só em edição) */}
          {goal && (
            <div>
              <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-2 block">Status</label>
              <div className="flex gap-2">
                {STATUSES.map(s => (
                  <button
                    key={s.value} type="button"
                    onClick={() => setForm(p => ({ ...p, status: s.value }))}
                    className={`flex-1 py-2 rounded-xl border text-sm font-600 transition-all cursor-pointer ${
                      form.status === s.value
                        ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
                        : 'text-gray-500 bg-white border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Personalizar ícone e cor */}
          <details className="group">
            <summary className="text-xs font-600 text-gray-400 cursor-pointer hover:text-gray-600 flex items-center gap-1.5 select-none list-none">
              <i className="ri-settings-3-line text-xs" />
              Personalizar ícone e cor
              <i className="ri-arrow-down-s-line text-xs group-open:rotate-180 transition-transform" />
            </summary>
            <div className="mt-3 space-y-3">
              <div>
                <p className="text-xs text-gray-500 mb-1.5">Ícone</p>
                <div className="grid grid-cols-6 gap-2">
                  {ICONS.map(ic => (
                    <button
                      key={ic} type="button"
                      onClick={() => setForm(p => ({ ...p, icon: ic }))}
                      className={`w-9 h-9 flex items-center justify-center rounded-xl border transition-all cursor-pointer ${
                        form.icon === ic ? 'border-emerald-400 bg-emerald-50 text-emerald-600' : 'border-gray-200 text-gray-500 hover:border-gray-300'
                      }`}
                    >
                      <i className={`${ic} text-base`} />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1.5">Cor</p>
                <div className="flex items-center gap-2">
                  {EXTRA_COLORS.map(c => (
                    <button
                      key={c} type="button"
                      onClick={() => setForm(p => ({ ...p, color: c }))}
                      style={{ backgroundColor: c }}
                      className={`w-7 h-7 rounded-full cursor-pointer transition-transform ${form.color === c ? 'scale-125 ring-2 ring-offset-2 ring-gray-400' : ''}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </details>

          {errorMsg && (
            <div className="flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600">
              <i className="ri-error-warning-line" />{errorMsg}
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-600 rounded-xl text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap">
              Cancelar
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-600 rounded-xl text-sm cursor-pointer whitespace-nowrap">
              {saving ? 'Salvando...' : goal ? 'Salvar alterações' : 'Criar meta'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
